import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { BookOpen } from 'lucide-react';

const PROGRAMS = ['CPNS', 'UTBK-SNBT', 'TNI', 'POLRI', 'SEKOLAH KEDINASAN', 'BUMN', 'TKA', 'SMA Favorit', 'Umum'];

export default function EbookDialog({ open, mode, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author: '',
    program: '',
    price: 0,
    original_price: '',
    cover_url: '',
    file_url: '',
    is_active: true,
    is_featured: false,
    whatsapp_number: ''
  });

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '',
        description: data.description || '',
        author: data.author || '',
        program: data.program || '',
        price: data.price || 0,
        original_price: data.original_price || '',
        cover_url: data.cover_url || '',
        file_url: data.file_url || '',
        is_active: data.is_active !== false,
        is_featured: data.is_featured || false,
        whatsapp_number: data.whatsapp_number || ''
      });
    } else {
      setFormData({
        title: '',
        description: '',
        author: '',
        program: '',
        price: 0,
        original_price: '',
        cover_url: '',
        file_url: '',
        is_active: true,
        is_featured: false,
        whatsapp_number: ''
      });
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseInt(formData.price) || 0,
      original_price: formData.original_price ? parseInt(formData.original_price) : null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#4472C4]" />
            {mode === 'edit' ? 'Edit eBook' : 'Tambah eBook Baru'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Judul eBook <span className="text-red-500">*</span></Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Contoh: Panduan Lengkap SKD CPNS 2026"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Deskripsi</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Deskripsi singkat tentang eBook..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Penulis</Label>
              <Input
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                placeholder="Nama penulis"
              />
            </div>

            <div className="space-y-2">
              <Label>Program</Label>
              <Select value={formData.program} onValueChange={(v) => setFormData({ ...formData, program: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih Program" />
                </SelectTrigger>
                <SelectContent>
                  {PROGRAMS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Harga (Rp)</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="0 = Gratis"
                min="0"
              />
            </div>

            <div className="space-y-2">
              <Label>Harga Asli (Opsional)</Label>
              <Input
                type="number"
                value={formData.original_price}
                onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                placeholder="Untuk tampilkan diskon"
                min="0"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>URL Cover Image</Label>
            <Input
              value={formData.cover_url}
              onChange={(e) => setFormData({ ...formData, cover_url: e.target.value })}
              placeholder="https://example.com/cover.jpg"
            />
          </div>

          <div className="space-y-2">
            <Label>URL File eBook (PDF)</Label>
            <Input
              value={formData.file_url}
              onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
              placeholder="https://example.com/ebook.pdf"
            />
          </div>

          <div className="space-y-2">
            <Label>Nomor WhatsApp (untuk kontak)</Label>
            <Input
              value={formData.whatsapp_number}
              onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
              placeholder="6281234567890"
            />
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label className="font-normal">Aktif</Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
              />
              <Label className="font-normal">Featured</Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button type="submit" disabled={!formData.title}>
              {mode === 'edit' ? 'Simpan Perubahan' : 'Tambah eBook'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
